// The module's one piece of state: what this workspace has done, and whether the
// tour is open.
//
// It sits above the routes so the drawer survives the redirect that `/start`
// performs — the tour is opened while deciding where to go, and is still open
// when we arrive.

import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import { useWorkspace } from "../lib/WorkspaceContext";
import { logError } from "../lib/logger";
import { NO_FACTS, type EngagementFacts } from "./facts";
import { loadProgress, progressFrom, type Progress, type ServerProgress } from "./progress";
import { isStripCollapsed, setStripCollapsed } from "./storage";

interface EngagementState {
  /** The counts have been answered (or have failed) for the active workspace. */
  ready: boolean;
  facts: EngagementFacts;
  /** Null when there is no workspace, or when the counts could not be read. */
  progress: Progress | null;
  refresh: () => Promise<void>;

  /** Which slide the drawer is showing, or null when it is closed. */
  tourSlide: number | null;
  openTour: (slide?: number) => void;
  closeTour: () => void;

  stripCollapsed: boolean;
  stripExpanded: boolean;
  collapseStrip: (collapsed: boolean) => void;
}

const Ctx = createContext<EngagementState | null>(null);

/** Facts are re-read on navigation, but never more often than this. */
const REFRESH_INTERVAL_MS = 15_000;

export function EngagementProvider({ children }: { children: ReactNode }) {
  // `seed` is the session's own answer for this workspace (ADR-0027): the first
  // checklist is already paid for, so asking again here would be a second round
  // trip for something we were just handed.
  const { active, ready: wsReady, progress: seed } = useWorkspace();
  const { pathname } = useLocation();

  const [server, setServer] = useState<ServerProgress | null>(null);
  const [failed, setFailed] = useState(false);
  const [ready, setReady] = useState(false);
  const [tourSlide, setTourSlide] = useState<number | null>(null);
  const [stripCollapsed, setCollapsed] = useState(false);
  const [expandedWorkspace, setExpandedWorkspace] = useState<string | null>(null);

  const workspaceId = active?.workspace_id ?? null;
  const lastLoad = useRef(0);

  const refresh = useCallback(async () => {
    if (!workspaceId) {
      setServer(null);
      setFailed(false);
      setReady(true);
      return;
    }
    lastLoad.current = Date.now();
    try {
      setServer(await loadProgress(workspaceId));
      setFailed(false);
    } catch (e) {
      // A failed read hides a suggestion. It must never look like a failure to
      // the person reading the screen, so it is logged and nothing is shown.
      setFailed(true);
      void logError("engagement", e, { step: "load_progress", workspaceId });
    } finally {
      setReady(true);
    }
  }, [workspaceId]);

  // Follow the active workspace. Switching workspace switches checklist, which
  // is the only correct behaviour when one person owns a business and works in
  // somebody else's.
  useEffect(() => {
    if (!wsReady) return;
    setTourSlide(null);
    setCollapsed(workspaceId ? isStripCollapsed(workspaceId) : false);

    // The session already answered for the workspace it made active, so the
    // first render costs nothing. Switching workspace later has no seed, and
    // asks.
    if (seed && workspaceId) {
      setServer(seed);
      setFailed(false);
      lastLoad.current = Date.now();
      setReady(true);
      return;
    }
    setReady(false);
    void refresh();
  }, [wsReady, workspaceId, seed, refresh]);

  // Connecting an account, publishing a post and uploading a file all happen on
  // other screens and none of them report back here. Re-reading on navigation
  // is what keeps the checklist honest; the interval is what stops that being
  // four queries per click.
  useEffect(() => {
    if (!ready || !workspaceId) return;
    if (Date.now() - lastLoad.current < REFRESH_INTERVAL_MS) return;
    void refresh();
  }, [pathname, ready, workspaceId, refresh]);

  const collapseStrip = useCallback((collapsed: boolean) => {
    setCollapsed(collapsed);
    setExpandedWorkspace(collapsed ? null : workspaceId);
    if (workspaceId) setStripCollapsed(workspaceId, collapsed);
  }, [workspaceId]);

  const openTour = useCallback((slide = 0) => setTourSlide(slide), []);
  const closeTour = useCallback(() => setTourSlide(null), []);

  const progress = useMemo<Progress | null>(
    () => (!workspaceId || failed || !server ? null : progressFrom(server)),
    [workspaceId, failed, server],
  );

  // Kept on the context because the strip and the drawer render the numbers.
  const facts = server?.facts ?? NO_FACTS;

  const value = useMemo<EngagementState>(() => ({
    ready, facts, progress, refresh,
    tourSlide, openTour, closeTour,
    stripCollapsed, stripExpanded: expandedWorkspace === workspaceId && workspaceId !== null, collapseStrip,
  }), [
    ready, facts, progress, refresh,
    tourSlide, openTour, closeTour,
    stripCollapsed, expandedWorkspace, workspaceId, collapseStrip,
  ]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useEngagement(): EngagementState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useEngagement must be used inside an EngagementProvider");
  return ctx;
}
